/**
 * Anforderungen eines Abschnitts als Objekte: feste Kennung, Verbindlichkeit, Zerlegung in Adressat,
 * Gegenstand, Handlung, Intervall und Pflichtart, verknüpfte Pflichten, Prüfstatus. Die Leitung
 * bestätigt oder verwirft, Autor und Leitung korrigieren die Zerlegung oder lassen neu zerlegen.
 * Mit Entwurf: was er an den Anforderungen ändern würde (gespeichert wird mit der Freigabe).
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Pencil, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "../lib/utils";
import { useGuidelinesAPI, type SectionRequirements, type StoredRequirement } from "./GuidelinesAPI";

const KIND_CLASS: Record<string, string> = {
  muss: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  soll: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100",
  kann: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100",
};
const ROLE_CODES = ["betreiber", "arbeitgeber", "eigentuemer", "veranlasser", "geschaeftsleitung", "verwalter", "arbeitnehmer", "auftragnehmer", "befaehigtePerson", "sachverstaendiger"];
const OBLIGATION_TYPES = ["inspection", "documentation", "notification", "maintenance", "operator", "training", "organizational", "conformity"];

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function RequirementsPanel(props: { customer: string; documentId: string; sectionId: string; canEdit: boolean; canReview: boolean; locked: boolean; refreshKey: string }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [data, setData] = useState<SectionRequirements | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<StoredRequirement>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.listRequirements(props.documentId, props.sectionId).then(setData, (e) => toast.error(errorText(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.documentId, props.sectionId]);
  useEffect(() => {
    setData(null);
    load();
  }, [load, props.refreshKey]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const analyze = () =>
    act(async () => {
      const r = await api.analyzeRequirements(props.documentId, props.sectionId);
      toast.success(t("guidelinesPage.requirements.analyzed", r));
    });

  const save = (r: StoredRequirement) =>
    act(async () => {
      await api.updateRequirement(props.documentId, r.key, {
        addressee: form.addressee ?? null,
        roleCode: form.roleCode || null,
        subject: form.subject ?? null,
        action: form.action ?? null,
        obligationType: form.obligationType || null,
        interval: form.interval && (form.interval.months || form.interval.text) ? form.interval : null,
      });
      setEditing(null);
    });

  if (!data) return <p className="text-sm text-muted-foreground">{t("guidelinesPage.requirements.loading")}</p>;
  const editable = props.canEdit && !props.locked;

  const interval = (r: StoredRequirement) =>
    r.interval
      ? [r.interval.months ? t("guidelinesPage.requirements.months", { count: r.interval.months }) : null, r.interval.trigger ? t(`guidelinesPage.requirements.trigger.${r.interval.trigger}`) : null, r.interval.text]
          .filter(Boolean)
          .join(" · ")
      : null;

  return (
    <div className="flex flex-col gap-2" data-testid="requirements">
      <div className="flex items-center gap-2">
        <p className="flex-1 text-xs text-muted-foreground">{t("guidelinesPage.requirements.intro", { count: data.items.length })}</p>
        {editable ? (
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={analyze} disabled={busy}>
            <RefreshCw size={13} className={cn("mr-1", busy && "animate-spin")} />
            {t("guidelinesPage.requirements.analyze")}
          </Button>
        ) : null}
      </div>
      {data.draft && (data.draft.changed.length || data.draft.created.length || data.draft.retired.length) ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs dark:border-amber-800 dark:bg-amber-950/40" data-testid="requirements-draft">
          {t("guidelinesPage.requirements.draftEffect", { created: data.draft.created.length, changed: data.draft.changed.length, retired: data.draft.retired.length })}
        </div>
      ) : null}
      {!data.items.length ? <p className="text-sm text-muted-foreground">{t("guidelinesPage.noRequirements")}</p> : null}
      {data.items.map((r) => {
        const changedInDraft = data.draft?.changed.some((c) => c.key === r.key);
        const retiredInDraft = data.draft?.retired.includes(r.key);
        return (
          <div key={r.key} className={cn("rounded-md border p-3 text-sm", r.status === "rejected" && "opacity-60")} data-requirement={r.key}>
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-mono tabular-nums">{r.key}</span>
              <span className={cn("rounded px-1.5", KIND_CLASS[r.kind])}>{t(`guidelinesPage.kind.${r.kind}`)}</span>
              <span className={cn("rounded px-1.5", r.status === "confirmed" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100" : "bg-muted")}>{t(`guidelinesPage.requirements.status.${r.status}`)}</span>
              {changedInDraft ? <span className="rounded bg-amber-100 px-1.5 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t("guidelinesPage.requirements.changedInDraft")}</span> : null}
              {retiredInDraft ? <span className="rounded bg-rose-100 px-1.5 text-rose-900 dark:bg-rose-900/40 dark:text-rose-100">{t("guidelinesPage.requirements.retiredInDraft")}</span> : null}
              {r.changedIn ? <span>{t("guidelinesPage.requirements.changedIn", { edition: r.changedIn })}</span> : null}
            </div>
            <div className="mb-2">{r.text}</div>
            {editing === r.key ? (
              <div className="grid grid-cols-[7rem_1fr] items-center gap-1 text-xs">
                <span>{t("guidelinesPage.requirements.field.addressee")}</span>
                <Input className="h-7" value={form.addressee ?? ""} onChange={(e) => setForm({ ...form, addressee: e.target.value })} />
                <span>{t("guidelinesPage.requirements.field.roleCode")}</span>
                <select className="h-7 rounded border bg-background px-1" value={form.roleCode ?? ""} onChange={(e) => setForm({ ...form, roleCode: e.target.value || null })}>
                  <option value="">—</option>
                  {ROLE_CODES.map((c) => (
                    <option key={c} value={c}>{t(`guidelinesPage.requirements.role.${c}`)}</option>
                  ))}
                </select>
                <span>{t("guidelinesPage.requirements.field.subject")}</span>
                <Input className="h-7" value={form.subject ?? ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                <span>{t("guidelinesPage.requirements.field.action")}</span>
                <Input className="h-7" value={form.action ?? ""} onChange={(e) => setForm({ ...form, action: e.target.value })} />
                <span>{t("guidelinesPage.requirements.field.interval")}</span>
                <div className="flex gap-1">
                  <Input className="h-7 w-20" type="number" min={1} placeholder={t("guidelinesPage.requirements.monthsShort")} value={form.interval?.months ?? ""} onChange={(e) => setForm({ ...form, interval: { ...(form.interval ?? {}), months: Number(e.target.value) || undefined } })} />
                  <Input className="h-7" placeholder={t("guidelinesPage.requirements.intervalText")} value={form.interval?.text ?? ""} onChange={(e) => setForm({ ...form, interval: { ...(form.interval ?? {}), text: e.target.value || undefined } })} />
                </div>
                <span>{t("guidelinesPage.requirements.field.obligationType")}</span>
                <select className="h-7 rounded border bg-background px-1" value={form.obligationType ?? ""} onChange={(e) => setForm({ ...form, obligationType: e.target.value || null })}>
                  <option value="">—</option>
                  {OBLIGATION_TYPES.map((c) => (
                    <option key={c} value={c}>{t(`guidelinesPage.requirements.type.${c}`)}</option>
                  ))}
                </select>
                <span />
                <div className="mt-1 flex justify-end gap-1">
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(null)}>{t("guidelinesPage.cancel")}</Button>
                  <Button size="sm" className="h-7" onClick={() => save(r)} disabled={busy}>{t("guidelinesPage.requirements.save")}</Button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-[7rem_1fr] gap-x-2 gap-y-0.5 text-xs">
                {[
                  [
                    "addressee",
                    r.addressee
                      ? [r.addressee, r.roleCode ? `(${t(`guidelinesPage.requirements.role.${r.roleCode}`)})` : null].filter(Boolean).join(" ")
                      : r.impliedAddressee
                        ? `${r.impliedAddressee} ${t("guidelinesPage.requirements.implied")}`
                        : null,
                  ],
                  ["subject", r.subject],
                  ["action", r.action],
                  ["interval", interval(r)],
                  ["obligationType", r.obligationType ? t(`guidelinesPage.requirements.type.${r.obligationType}`) : null],
                ].map(([field, value]) => (
                  <div key={field as string} className="contents">
                    <dt className="text-muted-foreground">{t(`guidelinesPage.requirements.field.${field}`)}</dt>
                    <dd>{value || <span className="text-muted-foreground">—</span>}</dd>
                  </div>
                ))}
                {r.obligationKeys.length ? (
                  <div className="contents">
                    <dt className="text-muted-foreground">{t("guidelinesPage.requirements.field.obligations")}</dt>
                    <dd className="flex flex-wrap gap-1">
                      {r.obligationKeys.map((k) => (
                        <span key={k} className="rounded bg-primary/10 px-1 font-mono text-[11px] text-primary">{k}</span>
                      ))}
                    </dd>
                  </div>
                ) : null}
              </dl>
            )}
            {editing !== r.key && (editable || (props.canReview && !props.locked)) ? (
              <div className="mt-2 flex justify-end gap-1">
                {editable ? (
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setEditing(r.key); setForm(r); }}>
                    <Pencil size={13} className="mr-1" />
                    {t("guidelinesPage.requirements.edit")}
                  </Button>
                ) : null}
                {props.canReview && !props.locked && r.status !== "confirmed" ? (
                  <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => act(() => api.updateRequirement(props.documentId, r.key, { status: "confirmed" }))} disabled={busy}>
                    <Check size={13} className="mr-1" />
                    {t("guidelinesPage.confirm")}
                  </Button>
                ) : null}
                {props.canReview && !props.locked && r.status !== "rejected" ? (
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => act(() => api.updateRequirement(props.documentId, r.key, { status: "rejected" }))} disabled={busy}>
                    <X size={13} className="mr-1" />
                    {t("guidelinesPage.reject")}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
